import { ReactNode } from 'react'
import { cn } from '../../utils'
import { MessageSquare, Video, Mic, Lock } from 'lucide-react'
import { Button } from './Button'

export interface UsageBreakdownItemData {
  icon: ReactNode
  label: string
  used: number
  total: number
  percentage: number
  unit?: string
  greyed?: boolean
}

export interface UsageItemProps extends UsageBreakdownItemData {
  onUpgrade?: () => void
}

/**
 * Single usage item showing feature consumption
 */
export const UsageBreakdownItem: React.FC<UsageItemProps> = ({
  icon,
  label,
  used,
  total,
  percentage,
  unit = 'credits',
  greyed = false,
  onUpgrade,
}) => {
  const isWarning = percentage >= 80 && percentage < 100
  const isLimit = percentage >= 100

  const progressColor = isLimit ? 'bg-red-80' : isWarning ? 'bg-yellow' : 'bg-light-80'

  return (
    <div className={cn('border-light-10 flex items-center gap-4 rounded-lg border p-4 transition-all')}>
      <div className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-lg')}>
        {greyed ? <Lock className="text-primary h-5 w-5" /> : <div className="text-primary">{icon}</div>}
      </div>

      <div className="min-w-0 flex-1">
        <div className="mb-1 flex items-center justify-between">
          <span className={cn('text-sm font-medium')}>{label}</span>
          {!greyed && (
            <span className="text-light-60 text-xs">
              {used.toFixed(unit === 'hours' ? 1 : 0)} / {total} {unit}
            </span>
          )}
        </div>

        {!greyed && (
          <div className="flex items-center gap-2">
            <div className="bg-light-10 h-2 flex-1 overflow-hidden rounded-full">
              <div
                className={cn('h-full rounded-full transition-all duration-500', progressColor)}
                style={{ width: `${Math.min(100, percentage)}%` }}
              />
            </div>
            <span
              className={cn(
                'min-w-[3ch] text-right text-xs font-medium',
                isLimit ? 'text-red-80' : isWarning ? 'text-yellow-subtle' : 'text-light-60',
              )}
            >
              {percentage}%
            </span>
          </div>
        )}

        {greyed && onUpgrade && (
          <Button size="small" variant="yellow-secondary" onClick={onUpgrade} className="mt-2">
            Upgrade to Unlock
          </Button>
        )}
      </div>
    </div>
  )
}

export interface UsageBreakdownListProps {
  items: UsageItemProps[]
}

/**
 * List of usage breakdown items
 */
export const UsageBreakdownList: React.FC<UsageBreakdownListProps> = ({ items }) => {
  return (
    <div className="flex flex-col gap-3">
      {items.map((item, index) => (
        <UsageBreakdownItem key={index} {...item} />
      ))}
    </div>
  )
}

// Export helper to get icon for feature type
export const getFeatureIcon = (feature: string): ReactNode => {
  switch (feature) {
    case 'premium-responses':
      return <MessageSquare className="h-5 w-5" />
    case 'Audio Note Minutes':
      return <Video className="h-5 w-5" />
    case 'dictation':
      return <Mic className="h-5 w-5" />
    default:
      return <MessageSquare className="h-5 w-5" />
  }
}
